import { useRef, useState } from "react";
import { teamBanner } from "../../../../functions/banners";
import PlayerSearch from "../../../../components/PlayerSearch";
import { useTournament, useTournamentUpdate } from "../../TournamentContext";
import { RiDeleteBinLine } from "react-icons/ri";
import { RiUserAddLine } from "react-icons/ri";
import {
  arrayRemove,
  arrayUnion,
  deleteField,
  doc,
  updateDoc,
} from "firebase/firestore";
import { firestore } from "../../../../firebase/config";
import Modal from "../../../../components/modals/Modal";
import { INPUT_SAVE_ICON_DELAY, SaveStatuses } from "../../../../constants";
import SaveIndicator from "../../../../components/SaveIndicator";

const TeamCard = ({ team, teamId }) => {
  const { profiles, tournament, isUserOrganizer, tournamentId } =
    useTournament();
  const { name, email, groupId, profileIds } = team;
  const { groups, tournamentType } = tournament;
  const { updateTeam } = useTournamentUpdate();

  const [localName, setLocalName] = useState(name || teamId);
  const [localEmail, setLocalEmail] = useState(email || "");
  const [localProfileIds, setLocalProfileIds] = useState(profileIds);
  const [teamSaveStatus, setTeamSaveStatus] = useState(SaveStatuses.Null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const teamDebounceTimer = useRef(null);
  const playerDebounceTimer = useRef(null);

  const debouncedSave = (field, value) => {
    if (!isUserOrganizer) return;

    clearTimeout(teamDebounceTimer.current);
    setTeamSaveStatus(SaveStatuses.Loading);

    teamDebounceTimer.current = setTimeout(async () => {
      const teamsRef = doc(firestore, "teams", tournamentId);

      try {
        await updateDoc(teamsRef, { [`${teamId}.${field}`]: value });
        setTeamSaveStatus(SaveStatuses.Success);
      } catch (error) {
        console.error("Error updating team:", error);
        setTeamSaveStatus(SaveStatuses.Error);
      } finally {
        setTimeout(() => {
          setTeamSaveStatus(SaveStatuses.Null);
        }, INPUT_SAVE_ICON_DELAY);
      }
    }, 800);
  };

  const handleNameChange = (e) => {
    const newName = e.target.value || "";
    setLocalName(newName);
    debouncedSave("name", newName);
  };

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setLocalEmail(newEmail);
    debouncedSave("email", newEmail);
  };

  const deleteTeam = async () => {
    const teamsRef = doc(firestore, "teams", tournamentId);

    try {
      await updateDoc(teamsRef, { [teamId]: deleteField() });

      updateTeam(teamId, null);
    } catch (error) {
      alert(error.message);
      console.log(error);
    }
  };

  const addPlayer = async () => {
    const teamsRef = doc(firestore, "teams", tournamentId);

    try {
      await updateDoc(teamsRef, { [`${teamId}.profileIds`]: arrayUnion("") });

      const profileIdsArray = profileIds ?? [];

      updateTeam(teamId, { ...team, profileIds: [...profileIdsArray, ""] });
    } catch (error) {
      alert(error.message);
      console.log(error);
    }
  };

  const deletePlayer = async (profileId) => {
    const teamsRef = doc(firestore, "teams", tournamentId);

    try {
      await updateDoc(teamsRef, {
        [`${teamId}.profileIds`]: arrayRemove(profileId),
      });

      updateTeam(teamId, {
        ...team,
        profileIds: profileIds.filter((id) => id !== profileId),
      });
    } catch (error) {
      alert(error.message);
      console.log(error);
    }
  };

  const updatePlayer = async (profileId, index) => {
    setTeamSaveStatus(SaveStatuses.Loading);
    const updatedProfileIds = [...localProfileIds];
    updatedProfileIds[index] = profileId;
    setLocalProfileIds(updatedProfileIds);

    playerDebounceTimer.current = setTimeout(async () => {
      const teamsRef = doc(firestore, "teams", tournamentId);

      try {
        await updateDoc(teamsRef, {
          [`${teamId}.profileIds`]: updatedProfileIds,
        });
        setTeamSaveStatus(SaveStatuses.Success);
      } catch (error) {
        alert(`Could not save player: ${error.message}`);
        setTeamSaveStatus(SaveStatuses.Error);
      } finally {
        setTimeout(() => {
          setTeamSaveStatus(SaveStatuses.Null);
        }, INPUT_SAVE_ICON_DELAY);
      }
    }, 800);
  };

  return (
    <>
      <div
        id={teamId}
        className="w-full max-w-xs bg-white shadow-xl rounded-lg border border-stone-300 h-full"
        data-testid={`team-card-${teamId}`}
      >
        <div
          className={`${teamBanner(
            groupId
          )} w-full p-1 relative flex justify-between rounded-t-lg`}
        >
          {isUserOrganizer && (
            <div className="flex flex-col justify-between">
              <RiUserAddLine
                className="w-5 h-5 pt-1 text-white cursor-pointer"
                onClick={() => addPlayer()}
              />

              <SaveIndicator saveStatus={teamSaveStatus} />
            </div>
          )}
          <div className="w-full flex flex-col text-white break-words gap-1">
            <input
              className="bg-transparent placeholder-white break-words text-center"
              data-testid={`team-card-name-input-${teamId}`}
              disabled={!isUserOrganizer}
              onChange={(event) => handleNameChange(event)}
              value={localName}
            />
            {isUserOrganizer && (
              <input
                className="bg-transparent placeholder-white text-xs break-words text-center"
                data-testid={`team-card-email-input-${teamId}`}
                disabled={!isUserOrganizer}
                onChange={(event) => handleEmailChange(event)}
                placeholder={"No email"}
                value={localEmail}
              />
            )}
          </div>

          {isUserOrganizer && !groups && (
            <RiDeleteBinLine
              className="w-5 h-5 pt-1 text-white cursor-pointer"
              onClick={() => setIsModalOpen(true)}
            />
          )}
        </div>

        <ul className="p-3 space-y-4">
          {profileIds?.map((profileId, index) => {
            const profile = profiles[profileId] || {};
            const { name, club, skill, pronouns } = profile;

            return (
              <li key={`${profileId}-${index}`}>
                {isUserOrganizer ? (
                  <div className="flex items-center">
                    <PlayerSearch
                      index={index}
                      playerName={name ?? profileId}
                      onChange={updatePlayer}
                      onSelect={updatePlayer}
                    />

                    <RiDeleteBinLine
                      className="w-4 h-4 text-gray-400 cursor-pointer ml-1"
                      onClick={() => deletePlayer(profileId)}
                    />
                  </div>
                ) : (
                  <p className="text-base font-medium text-gray-800 truncate mb-1">
                    {name ?? profileId}
                  </p>
                )}

                <div className="flex justify-between gap-2 mt-1 mx-1 flex-wrap text-xs">
                  <div className="text-sm text-gray-500 truncate">{club}</div>

                  {isUserOrganizer && (
                    <div>
                      {pronouns && (
                        <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                          {pronouns}
                        </span>
                      )}
                      {skill && tournamentType === "MIXER" && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                          {skill}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <Modal
        isOpen={isModalOpen}
        title={`Delete ${name}`}
        subtitle={"This action cannot be undone"}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={deleteTeam}
      >
        You sure about that?
      </Modal>
    </>
  );
};

export default TeamCard;
